import React, {
  createContext, useContext, useState, useRef,
  useCallback, useEffect, useMemo,
} from 'react';
import ConfirmModal from '../features/scope_module/components/ConfirmModal';

// Mock API for demo purposes
const api = {
  fetchSecureImage: async (url) => url,
  getSessionDetails: async (id) => ({
    id,
    image_pair: {
      id: 'demo-pair-1',
      comparison_job_id: 'demo-job-1',
    },
    pair_type: 'synthetic',
    setName: 'Demo Set',
    designatedConclusion: 'Identification',
    conclusionScale: 5,
  }),
  getSyntheticPair: async (id) => ({
    id,
    stitchedEnhancedUrl: '/demo-images/stitched_enhanced.png',
    stitchedClarityMapUrl: '/demo-images/stitched_clarity_map.png',
    processingStatus: 'completed',
    setName: 'Demo Synthetic Set',
    syntheticPairType: 'demo-type',
    designatedConclusion: 'Identification',
    conclusionScale: 5,
    latentSource: 'demo-latent',
    knownSource: 'demo-known',
  }),
  getImagePairDetails: async (id) => ({
    id,
    stitchedEnhancedUrl: '/demo-images/stitched_enhanced.png',
    stitchedClarityMapUrl: '/demo-images/stitched_clarity_map.png',
    processingStatus: 'completed',
    setName: 'Demo Image Pair Set',
    designatedConclusion: 'Identification',
    conclusionScale: 5,
    isGlobal: false,
    agencyName: 'Demo Agency',
    notes: 'Demo notes',
  }),
  updateJobStatus: async (jobId, status) => { console.log(`[DEMO] Job ${jobId} status updated to ${status}`); },
  submitConclusion: async (jobId, conclusion, status) => { console.log(`[DEMO] Job ${jobId} conclusion ${conclusion} submitted with status ${status}`); },
  saveTrackingData: async (sessionId, data) => {
    console.log(`[DEMO] Tracking data for session ${sessionId} saved:`, data?.path?.length, 'path points,', data?.features?.length, 'features');
    // Actually save the data to sessionStorage so it can be retrieved later
    try {
      sessionStorage.setItem(`demoTrackingData_${sessionId}`, JSON.stringify(data));
    } catch (e) {
      console.warn('[DEMO] Failed to save tracking data:', e);
    }
  },
  getTrackingData: async (sessionId) => {
    console.log(`[DEMO] Getting tracking data for session ${sessionId}`);
    // First try the specific session key
    const sessionData = sessionStorage.getItem(`demoTrackingData_${sessionId}`);
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      console.log(`[DEMO] Found tracking data:`, parsed?.path?.length, 'path points');
      return parsed;
    }

    // Fall back to legacy key
    const data = sessionStorage.getItem('scopeReviewContext');
    if (data) {
        const parsed = JSON.parse(data);
        return parsed;
    }

    return {
      path: [],
      features: [],
    };
  },
  saveUserStats: async (sessionId, stats) => { console.log(`[DEMO] User stats for session ${sessionId} saved.`); },
  analyzeSession: async (sessionId) => {
    console.log(`[DEMO] Analyzing session ${sessionId}`);
    return {
      metrics: {
        equilibrium_metrics: {
          ee_ratio: 0.6,
          aa_ratio: 0.4,
          er_ratio: 0.5,
          _source: 'demo-local',
        }
      }
    };
  },
};

const HEAT = {
  targetCols: 384,
  binSizeMin: 3,
  dwellMult: 1.25,
  sigmaPx: 24,
  normCap: 1500,
  intensityGamma: 0.85,
  rebuildStepScale: 0.75,
};

const DEBUG_HEATMAP = false;
const HDBG  = (...a) => DEBUG_HEATMAP && console.log('[HEATMAP]', ...a);
const HWARN = (...a) => DEBUG_HEATMAP && console.warn('[HEATMAP]', ...a);

export const MousePositionContext = createContext(null);
const DemoScopeContext = createContext(null);

export const useScope = () => useContext(DemoScopeContext);
export const useMousePosition = () => useContext(MousePositionContext);

const initialLayerConfig = {
  base:      { name: 'Base Layer',         visible: false, opacity: 1.0,  disabled: false, image: null },
  minutiae:  { name: 'Minutiae Layer',     visible: true,  opacity: 1.0,  disabled: true,  currentSession: 'all', image: null },
  heatmap:   { name: 'Heatmap Layer',      visible: false, opacity: 1.0,  disabled: true,  currentSession: 'all', image: null },
  heatmapAdd:    { name: 'Heatmap (Add)',    visible: false, opacity: 1.0, disabled: true,  currentSession: 'all', image: null },
  heatmapDelete: { name: 'Heatmap (Delete)', visible: false, opacity: 1.0, disabled: true,  currentSession: 'all', image: null },
  tracking:  { name: 'Tracking Layer',     visible: false, opacity: 1.0,  disabled: true,  currentSession: 'all', image: null },
  clarity:   { name: 'Clarity Map',        visible: false, opacity: 0.5,  disabled: false, image: null },
  quality:   { name: 'Quality Assessment', visible: false, opacity: 0.9,  disabled: true,  image: null },
  playback:  { name: 'Playback Layer',     visible: false, opacity: 1.0,  disabled: true,  currentSession: 'all', image: null },
};

const CONCLUSION_MAP = {
  3: ['Identification', 'Inconclusive', 'Exclusion'],
  5: ['Identification', 'Support for Identification', 'Inconclusive', 'Support for Exclusion', 'Exclusion'],
};

function looksLikeEquilibriumMetrics(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const k = Object.keys(obj);
  const hasEE = k.some(x =>
    x === 'ee_ratio' || x === 'exploration_exploitation' || x === 'explorationVsExploitation' || x === 'ee'
  );
  const hasAA = k.some(x =>
    x === 'aa_ratio' || x === 'assimilation_accommodation' || x === 'assimilationVsAccommodation' || x === 'aa'
  );
  const hasER = k.some(x =>
    x === 'er_ratio' || x === 'efficiency_resilience' || x === 'efficiencyVsResilience' || x === 'er'
  );
  return (hasEE && hasAA) || (hasEE && hasER) || (hasAA && hasER);
}

function pickEquilibriumMetrics(res) {
  const candidates = [
    res?.metrics?.equilibrium_metrics,
    res?.metrics?.equilibrium,
    res?.equilibrium_metrics,
    res?.equilibrium,
    res?.data?.metrics?.equilibrium_metrics,
    res?.data?.equilibrium_metrics,
    res?.result?.metrics?.equilibrium_metrics,
    res?.result?.equilibrium_metrics,
    res?.analysis?.metrics?.equilibrium_metrics,
    res?.analysisResults?.metrics?.equilibrium_metrics,
    res?.analysis_results?.metrics?.equilibrium_metrics,
    res?.analysis_results?.equilibrium_metrics,
    res?.analysisResults?.equilibrium_metrics,
    res?.payload?.metrics?.equilibrium_metrics,
    res?.payload?.equilibrium_metrics,
    res?.metrics,
  ];
  for (const c of candidates) {
    if (c && typeof c === 'object' && looksLikeEquilibriumMetrics(c)) return c;
  }
  const seen = new Set();
  const stack = [res];
  let depth = 0;
  while (stack.length && depth < 5) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    if (looksLikeEquilibriumMetrics(cur)) return cur;
    for (const v of Object.values(cur)) if (v && typeof v === 'object') stack.push(v);
    depth++;
  }
  return null;
}

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));

function computeLocalEquilibriumMetrics(path, events) {
  if (!Array.isArray(path) || path.length < 2) {
    return {
      ee_ratio: 0.5, aa_ratio: 0.5, er_ratio: 0.5,
      ee_stability: 0.5, aa_stability: 0.5, er_stability: 0.5,
      _source: 'local'
    };
  }

  const V_MOVE = 30; // px/s

  let movingTime = 0, dwellTime = 0, stateSwitches = 0;
  let prevMoving = null;

  let totalLen = 0;
  const netDx = path[path.length - 1].x - path[0].x;
  const netDy = path[path.length - 1].y - path[0].y;

  let prev = path[0];
  let prevVec = null;
  let turnSum = 0, turnCount = 0;

  for (let i = 1; i < path.length; i++) {
    const a = prev, b = path[i];
    const dt = Math.max(0, (b.t ?? 0) - (a.t ?? 0)) / 1000;
    const dx = b.x - a.x, dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);

    if (dt > 0) {
      const v = dist / dt;
      const moving = v > V_MOVE;
      if (prevMoving !== null && moving !== prevMoving) stateSwitches++;
      if (moving) movingTime += dt; else dwellTime += dt;
      prevMoving = moving;
    }

    totalLen += dist;

    const vec = { x: dx, y: dy };
    if (prevVec) {
      const dot = prevVec.x * vec.x + prevVec.y * vec.y;
      const mag = Math.hypot(prevVec.x, prevVec.y) * Math.hypot(vec.x, vec.y);
      if (mag > 1e-6) {
        const ang = Math.acos(Math.max(-1, Math.min(1, dot / mag)));
        turnSum += ang;
        turnCount++;
      }
    }
    prevVec = vec;
    prev = b;
  }

  const segs = path.length - 1;
  const ee_ratio = (movingTime + dwellTime) ? (dwellTime / (movingTime + dwellTime)) : 0.5;
  const ee_stability = segs > 1 ? (1 - Math.min(1, stateSwitches / (segs - 1))) : 0.5;

  let adds = 0, dels = 0, typeSwitches = 0; let prevType = null;
  for (const ev of Array.isArray(events) ? events : []) {
    if (ev.type === 'add') adds++;
    else if (ev.type === 'delete') dels++;
    if (ev.type === 'add' || ev.type === 'delete') {
      if (prevType && prevType !== ev.type) typeSwitches++;
      prevType = ev.type;
    }
  }
  const totalEv = adds + dels;
  const aa_ratio = totalEv ? (dels / totalEv) : 0.5;
  const aa_stability = totalEv > 1 ? (1 - Math.min(1, typeSwitches / (totalEv - 1))) : 0.5;

  const netDisp = Math.hypot(netDx, netDy);
  const straightness = totalLen > 0 ? (netDisp / totalLen) : 0;
  const deleteRate = totalEv ? (dels / totalEv) : 0;
  const efficiency = clamp01(0.7 * straightness + 0.3 * (1 - deleteRate));
  const meanTurn = turnCount ? (turnSum / turnCount) : 0;
  const er_ratio = clamp01(1 - efficiency);
  const er_stability = clamp01(1 - (meanTurn / Math.PI));

  return {
    ee_ratio, aa_ratio, er_ratio,
    ee_stability, aa_stability, er_stability,
    _source: 'local'
  };
}

export const DemoScopeProvider = ({ children, onExit }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);

  const [layersConfig, setLayersConfig] = useState(initialLayerConfig);

  const [isTracking, setIsTracking] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [hasTrackingCompleted, setHasTrackingCompleted] = useState(false);

  const [selectedConclusion, setSelectedConclusion] = useState('');
  const [conclusionOptions, setConclusionOptions] = useState([]);

  const [toolState, setToolState] = useState({ current: 'add' });
  const toolStateRef = useRef(toolState);

  const [analysisResults, setAnalysisResults] = useState(null);
  const [playbackState, setPlaybackState] = useState({
    isPlaying: false, speed: 1, currentTime: 0, duration: 0, progress: 0
  });
  const [hasCompleted, setHasCompleted] = useState(false);
  const [analysisEnabled, setAnalysisEnabled] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: 'info' });
  const statusMessageTimerRef = useRef(null);

  const [loadedImages, setLoadedImages] = useState({
    enhanced: null, clarity: null, quality: null,
    heatmap: null, heatmapAdd: null, heatmapDelete: null,
  });

  const [showEnhanced, setShowEnhanced] = useState(false);
  const [showClarityMap, setShowClarityMap] = useState(false);

  // ----- Review mode state -----
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isSyntheticPreview, setIsSyntheticPreview] = useState(false);
  const [isImagePairPreview, setIsImagePairPreview] = useState(false);
  const [layersReady, setLayersReady] = useState(false);
  const [metrics, setMetrics] = useState(null);

  // ----- Confirm Modal state -----
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: 'Confirm Action',
    message: 'Are you sure you want to proceed?',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
    onConfirm: null,
  });

  // ----- Features -----
  const [features, setFeatures] = useState([]);

  // ----- Mouse/tracking -----
  const dragRef = useRef({ isDown: false, startX: 0, startY: 0, lastX: 0, lastY: 0 });
  const hudMouseRef = useRef({ x: null, y: null });

  // ----- Sessions -----
  const [toolSessions, setToolSessions] = useState([]);
  const activeSessionIdRef = useRef(null);

  // ----- Heatmap -----
  const heatmapCountsRef = useRef(null);
  const heatAddCountsRef = useRef(null);
  const heatDelCountsRef = useRef(null);
  const heatmapMetaRef = useRef({ width: 0, height: 0, gridW: 0, gridH: 0, binSize: 4, sigmaPx: 30, max: 0, version: 0 });
  const heatmapOffscreenRef = useRef(null);

  const lastHeatTimeRef = useRef(null);
  const pendingPosRef = useRef(null);
  const heatLoopRafRef = useRef(0);

  // ----- Playback -----
  const playbackPathRef = useRef([]);
  const playbackSelectionRef = useRef('all');
  const visibilitySnapshotRef = useRef(null);
  const playbackRafRef = useRef(0);
  const playbackLastTsRef = useRef(0);

  const NOW = () => performance.now();

  const showToast = useCallback((text, duration = 3000, type = 'info') => {
    if (statusMessageTimerRef.current) clearTimeout(statusMessageTimerRef.current);
    setStatusMessage(prev => {
      const same = prev && prev.text === text && prev.type === type;
      return same ? prev : { text, type };
    });
    if (duration > 0) {
      statusMessageTimerRef.current = setTimeout(() => {
        setStatusMessage(prev => (prev.text === text && prev.type === type ? { text: '', type: 'info' } : prev));
      }, duration);
    }
  }, []);

  const updateLayer = useCallback((layerId, property, value) => {
    setLayersConfig(prev => ({ ...prev, [layerId]: { ...prev[layerId], [property]: value } }));
  }, []);

  const loadImage = useCallback(async (srcOrBlobUrl) => {
    const secureUrl = await api.fetchSecureImage(srcOrBlobUrl);
    const urlToUse = secureUrl || srcOrBlobUrl;
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${urlToUse}`));
      img.src = urlToUse;
    });
  }, []);

  const startNewSession = useCallback((type) => {
    const s = { id: Date.now(), type, events: [], pathPoints: [] };
    setToolSessions(prev => [...prev, s]);
    activeSessionIdRef.current = s.id;
    return s.id;
  }, []);

  const ensureActiveSession = useCallback(() => {
    if (activeSessionIdRef.current == null) {
      return startNewSession(toolStateRef.current.current);
    }
    return activeSessionIdRef.current;
  }, [startNewSession]);

  const appendPathPoint = useCallback((x, y, t) => {
    setToolSessions(prev => {
      const id = ensureActiveSession();
      return prev.map(s => (s.id === id ? { ...s, pathPoints: [...s.pathPoints, { x, y, t }] } : s));
    });
  }, [ensureActiveSession]);

  const pushSessionEvent = useCallback((type, x, y, t, extra = {}) => {
    setToolSessions(prev => {
      const id = ensureActiveSession();
      return prev.map(s => (s.id === id ? { ...s, events: [...s.events, { type, x, y, t, ...extra }] } : s));
    });
  }, [ensureActiveSession]);
  
  const getSessionBounds = useCallback((session) => {
    if (!session) return null;

    // Try to get bounds from events first
    if (session.events && session.events.length > 0) {
      const sorted = [...session.events].sort((a, b) => a.t - b.t);
      return { t0: sorted[0].t, t1: sorted[sorted.length - 1].t };
    }

    // Fall back to path points if no events
    if (session.pathPoints && session.pathPoints.length > 0) {
      const sorted = [...session.pathPoints].sort((a, b) => a.t - b.t);
      return { t0: sorted[0].t, t1: sorted[sorted.length - 1].t };
    }

    return null;
  }, []);

  const cleanTrackingData = useCallback((session) => {
    if (!session || !session.pathPoints || session.pathPoints.length === 0) return [];

    const bounds = getSessionBounds(session);
    if (!bounds) return session.pathPoints || [];

    const { t0, t1 } = bounds;
    return session.pathPoints.filter(p => p.t >= t0 && p.t <= t1);
  }, [getSessionBounds]);

  const normalizeSelection = useCallback((sel) => {
    if (!sel || sel === 'all') return 'all';
    if (Array.isArray(sel)) return sel.map(v => Number(v)).filter(n => Number.isFinite(n));
    const n = Number(sel);
    return Number.isFinite(n) ? [n] : 'all';
  }, []);

  const buildTrimmedPathForSelection = useCallback((selection) => {
    const sel = selection == null ? 'all' : normalizeSelection(selection);
    const sessions = toolSessions;
    const ids = sel === 'all' ? sessions.map(s => s.id) : sel;

    const result = [];
    sessions.forEach(s => {
      if (sel !== 'all' && !ids.includes(s.id)) return;
      const pts = cleanTrackingData(s);
      for (let i = 0; i < pts.length; i++) {
        result.push({ ...pts[i], mode: s.type, sessionId: s.id });
      }
    });
    return result;
  }, [toolSessions, normalizeSelection, cleanTrackingData]);

  const buildTrimmedEventsForSelection = useCallback((selection) => {
    const sel = selection == null ? 'all' : normalizeSelection(selection);
    const sessions = toolSessions;
    const ids = sel === 'all' ? sessions.map(s => s.id) : sel;

    const events = [];
    sessions.forEach(s => {
      if (sel !== 'all' && !ids.includes(s.id)) return;
      const bounds = getSessionBounds(s);
      if (!bounds) return;
      const { t0, t1 } = bounds;
      (s.events || []).forEach(ev => {
        if (ev.t >= t0 && ev.t <= t1) {
          events.push({ ...ev, sessionId: s.id });
        }
      });
    });
    events.sort((a, b) => a.t - b.t);
    return events;
  }, [toolSessions, normalizeSelection, getSessionBounds]);

  const canStartTracking = useMemo(() =>
    !isLoading && !isProcessing && !isTracking &&
    !!loadedImages.enhanced && !!loadedImages.clarity && !hasCompleted
  , [isLoading, isProcessing, isTracking, loadedImages, hasCompleted]);

  const canComplete = useMemo(() =>
    hasTrackingCompleted && !!selectedConclusion && !hasCompleted
  , [hasTrackingCompleted, selectedConclusion, hasCompleted]);

  const canAnalyze = useMemo(() =>
    hasCompleted && !analysisEnabled
  , [hasCompleted, analysisEnabled]);
    
  const canEndSession = useMemo(() => hasCompleted, [hasCompleted]);

  const startTracking = useCallback(async () => {
    if (!canStartTracking) return;

    setIsProcessing(true);
    try {
      setSessionStartTime(Date.now());
      setHasTrackingCompleted(false);
      setHasCompleted(false);
      setAnalysisEnabled(false);
      setSelectedConclusion('');
      setConclusionOptions([]);
      setIsTracking(true);
      setShowEnhanced(true);
      updateLayer('base', 'visible', true);

      setToolSessions([]);
      activeSessionIdRef.current = null;
      startNewSession(toolStateRef.current.current);

      // During tracking, only enable minutiae layer - tracking path should NOT be visible
      // The tracking path will only be revealed after Analyze is pressed
      updateLayer('tracking', 'visible', false);
      updateLayer('tracking', 'disabled', true);
      updateLayer('minutiae', 'visible', true);
      updateLayer('minutiae', 'disabled', false);

      pendingPosRef.current = null;

      const m = heatmapMetaRef.current;
      const totalLen = m.gridW * m.gridH || 0;
      heatmapCountsRef.current = new Float32Array(totalLen);
      heatAddCountsRef.current = new Float32Array(totalLen);
      heatDelCountsRef.current = new Float32Array(totalLen);
      heatmapMetaRef.current = { ...m, max: 0, version: (m.version + 1) | 0 };

      lastHeatTimeRef.current = performance.now();
      if (!heatLoopRafRef.current) {
        heatLoopRafRef.current = requestAnimationFrame(function tick() {
          const meta = heatmapMetaRef.current;
          const pos = pendingPosRef.current;
          if (meta.gridW && pos) {
            const now = performance.now();
            const last = lastHeatTimeRef.current ?? now;
            const dt = Math.max(0, now - last);
            lastHeatTimeRef.current = now;

            if (dt > 0) {
              const bx = Math.max(0, Math.min(meta.gridW - 1, Math.floor(pos.x / meta.binSize)));
              const by = Math.max(0, Math.min(meta.gridH - 1, Math.floor(pos.y / meta.binSize)));
              const idx = by * meta.gridW + bx;

              const total = heatmapCountsRef.current;
              const add   = heatAddCountsRef.current;
              const del   = heatDelCountsRef.current;
              if (total && add && del) {
                const inc = dt * HEAT.dwellMult;
                total[idx] += inc;
                const mode = toolStateRef.current.current === 'delete' ? 'delete' : 'add';
                if (mode === 'add') add[idx] += inc; else del[idx] += inc;
                if (total[idx] > meta.max) meta.max = total[idx];
                meta.version = (meta.version + 1) | 0;
              }
            }
          }
          if (isTracking) heatLoopRafRef.current = requestAnimationFrame(tick);
          else heatLoopRafRef.current = 0;
        });
      }

      showToast('Tracking Started.', 2000, 'success');
    } catch (err) {
      showToast(`Error starting tracking: ${err.message}`, 5000, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [canStartTracking, updateLayer, startNewSession, showToast]);

  const stopHeatLoop = useCallback(() => {
    if (heatLoopRafRef.current) {
      cancelAnimationFrame(heatLoopRafRef.current);
      heatLoopRafRef.current = 0;
    }
    lastHeatTimeRef.current = null;
  }, []);

  const persistSessionSignals = useCallback(async () => {
    try {
      const trimmedPath = buildTrimmedPathForSelection('all');
      const payload = {
        path: trimmedPath.map(p => ({ x: p.x, y: p.y, t: Math.round(p.t) })),
        features: features.map(f => ({ x: f.x, y: f.y, angleDeg: f.angleDeg })),
      };
      if (sessionDetails?.id) {
        await api.saveTrackingData(sessionDetails.id, payload);
      }
    } catch (_) {}
  }, [features, sessionDetails, buildTrimmedPathForSelection]);

  const stopTrackingNow = useCallback(async () => {
    if (!isTracking) { setHasTrackingCompleted(true); return; }
    setIsProcessing(true);
    try {
      setIsTracking(false);
      stopHeatLoop();
      setHasTrackingCompleted(true);

      await persistSessionSignals();

      updateLayer('heatmap',       'visible', false);
      updateLayer('heatmapAdd',    'visible', false);
      updateLayer('heatmapDelete', 'visible', false);
      updateLayer('tracking',      'visible', false);

      showToast('Tracking stopped. Select a conclusion and complete when ready.', 3000, 'info');
    } catch (err) {
      showToast(`Error stopping tracking: ${err.message}`, 5000, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [isTracking, persistSessionSignals, showToast, stopHeatLoop, updateLayer]);

  const detectConclusionScale = useCallback((details) => {
    const candidates = [
      details?.conclusionScale,
      details?.settings?.conclusionScale,
      details?.scope?.conclusionScale,
      details?.image_pair?.conclusionScale,
      Array.isArray(details?.conclusionOptions) ? details.conclusionOptions.length : undefined,
    ];
    for (const c of candidates) {
      if (c === 3 || c === 5) return c;
      if (c === '3-scale') return 3;
      if (c === '5-scale') return 5;
    }
    return 5;
  }, []);

  const resolveConclusionOptions = useCallback((details) => {
    if (Array.isArray(details?.conclusionOptions) && details.conclusionOptions.length > 0) {
      return details.conclusionOptions;
    }
    const scale = detectConclusionScale(details);
    return CONCLUSION_MAP[scale] || CONCLUSION_MAP[5];
  }, [detectConclusionScale]);

  useEffect(() => {
    if (!hasTrackingCompleted) return;
    const opts = resolveConclusionOptions(sessionDetails || {});
    setConclusionOptions(opts);
    setSelectedConclusion(prev => (opts.includes(prev) ? prev : ''));
  }, [hasTrackingCompleted, sessionDetails, resolveConclusionOptions]);

  const handleToggleTool = useCallback(() => {
    if (!isTracking) return;
    const nextTool = toolStateRef.current.current === 'add' ? 'delete' : 'add';
    setToolState({ current: nextTool });
    toolStateRef.current = { current: nextTool };
    startNewSession(nextTool);
    showToast(`Switched to ${nextTool} mode.`, 1500, 'info');
  }, [isTracking, startNewSession, showToast]);

  const handleSelectConclusion = useCallback((conclusion) => {
    setSelectedConclusion(conclusion);
  }, []);
    
  const computeUserStats = useCallback(() => {
    const pathAll = buildTrimmedPathForSelection('all');
    const eventsAll = buildTrimmedEventsForSelection('all');

    let adds = 0, dels = 0;
    for (const ev of eventsAll) {
      if (ev.type === 'add') adds++;
      else if (ev.type === 'delete') dels++;
    }

    let durationMs = 0;
    let lengthPx = 0;
    if (pathAll.length >= 2) {
      durationMs = Math.max(0, (pathAll[pathAll.length - 1].t - pathAll[0].t));
      for (let i = 1; i < pathAll.length; i++) {
        const a = pathAll[i - 1], b = pathAll[i];
        lengthPx += Math.hypot(b.x - a.x, b.y - a.y);
      }
    }

    const eq = computeLocalEquilibriumMetrics(pathAll, eventsAll);

    return {
      adds, deletes: dels,
      features_count: features.length,
      path_points: pathAll.length,
      path_duration_ms: Math.round(durationMs),
      path_length_px: Math.round(lengthPx),
      ee_ratio: eq.ee_ratio, aa_ratio: eq.aa_ratio, er_ratio: eq.er_ratio,
      ee_stability: eq.ee_stability, aa_stability: eq.aa_stability, er_stability: eq.er_stability,
      concluded_as: selectedConclusion || null,
      completed_at: new Date().toISOString(),
      _version: 1
    };
  }, [buildTrimmedPathForSelection, buildTrimmedEventsForSelection, features.length, selectedConclusion]);

  const handleCompleteSession = useCallback(async () => {
    if (!hasTrackingCompleted) { showToast('End tracking before completing the session.', 3000, 'info'); return; }
    if (!selectedConclusion)   { showToast('Select a conclusion to complete the session.', 3000, 'info'); return; }
    if (hasCompleted)          { showToast('Session already completed.', 2000, 'info'); return; }

    setIsProcessing(true);
    showToast('Completing session...', 0, 'loading');
    try {
      await persistSessionSignals();
      setHasCompleted(true);
      showToast('Session completed. You can now Analyze.', 4000, 'success');
    } catch (err) {
      showToast(`Error completing session: ${err.message}`, 5000, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [hasTrackingCompleted, hasCompleted, selectedConclusion, showToast, persistSessionSignals, computeUserStats]);
    
  const revealLayersForAnalysis = useCallback(() => {
    // Enable all layers for analysis - users can toggle any layer on/off
    setLayersConfig(prev => ({
      ...prev,
      base:         { ...prev.base,         disabled: false, visible: true },
      minutiae:     { ...prev.minutiae,     disabled: false, visible: true },
      heatmap:      { ...prev.heatmap,      disabled: false, visible: true,  opacity: 1.0 },
      heatmapAdd:   { ...prev.heatmapAdd,   disabled: false, visible: false, opacity: 1.0 },
      heatmapDelete:{ ...prev.heatmapDelete,disabled: false, visible: false, opacity: 1.0 },
      tracking:     { ...prev.tracking,     disabled: false, visible: true },
      clarity:      { ...prev.clarity,      disabled: false, visible: !!loadedImages.clarity },
      quality:      { ...prev.quality,      disabled: false, visible: true, opacity: 0.9 },
      playback:     { ...prev.playback,     disabled: false, visible: false, currentSession: 'all' },
    }));
  }, [loadedImages]);

  const handleEndSession = useCallback(async () => {
    showToast('Ending session...', 0, 'loading');
    if (onExit) onExit();
  }, [onExit, showToast]);

  const showConfirmModal = useCallback((options = {}) => {
    setConfirmModal({
      isOpen: true,
      title: options.title || 'Confirm Action',
      message: options.message || 'Are you sure you want to proceed?',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      variant: options.variant || 'danger',
      onConfirm: options.onConfirm || null,
    });
  }, []);

  const closeConfirmModal = useCallback(() => {
    setConfirmModal(prev => ({ ...prev, isOpen: false, onConfirm: null }));
  }, []);

  const handleConfirmModalConfirm = useCallback(() => {
    if (confirmModal.onConfirm) {
      confirmModal.onConfirm();
    }
    closeConfirmModal();
  }, [confirmModal.onConfirm, closeConfirmModal]);

  const handleCancelSession = useCallback(async () => {
    showConfirmModal({
      title: 'Cancel Session',
      message: 'Are you sure you want to cancel this session? All progress will be lost.',
      confirmText: 'Cancel Session',
      cancelText: 'Continue',
      variant: 'danger',
      onConfirm: () => {
        showToast('Session has been cancelled.', 3000, 'info');
        if (onExit) onExit();
      },
    });
  }, [onExit, showToast, showConfirmModal]);
  
  useEffect(() => { toolStateRef.current = toolState; }, [toolState]);
  useEffect(() => () => { if (statusMessageTimerRef.current) clearTimeout(statusMessageTimerRef.current); }, []);

  useEffect(() => {
    let timerId;
    if (isTracking && sessionStartTime) {
      timerId = setInterval(() => setElapsedTime(Date.now() - sessionStartTime), 1000);
    }
    return () => clearInterval(timerId);
  }, [isTracking, sessionStartTime]);
    
  const ensureOffscreenMatchesBase = useCallback(() => {
    const base = loadedImages?.enhanced;
    if (!base) return;
    const off = heatmapOffscreenRef.current;
    if (!off || off.width !== base.width || off.height !== base.height) {
      heatmapOffscreenRef.current = document.createElement('canvas');
      heatmapOffscreenRef.current.width = base.width;
      heatmapOffscreenRef.current.height = base.height;
      HDBG('Reallocated offscreen to', base.width, base.height);
    }
  }, [loadedImages]);

  const renderCountsToOffscreen = useCallback((counts) => {
    ensureOffscreenMatchesBase();

    const meta = heatmapMetaRef.current;
    const off = heatmapOffscreenRef.current;
    if (!off || !counts || !meta.gridW) { HWARN('renderCountsToOffscreen: missing buffers/meta'); return; }

    const { binSize, gridW, gridH } = meta;
    const octx = off.getContext('2d', { willReadFrequently: true });
    octx.clearRect(0, 0, off.width, off.height);

    let arrMax = 0;
    for (let i = 0; i < counts.length; i++) if (counts[i] > arrMax) arrMax = counts[i];
    if (!Number.isFinite(arrMax) || arrMax <= 0) {
      HDBG('renderCountsToOffscreen: array max <= 0');
      return;
    }

    const bins = document.createElement('canvas');
    bins.width = off.width;
    bins.height = off.height;
    const bctx = bins.getContext('2d');
    bctx.clearRect(0, 0, bins.width, bins.height);

    const safeMax = (HEAT.normCap != null) ? Math.max(1, Math.min(arrMax, HEAT.normCap)) : arrMax;

    for (let by = 0; by < gridH; by++) {
      for (let bx = 0; bx < gridW; bx++) {
        const v = counts[by * gridW + bx];
        if (v <= 0) continue;
        const t = Math.max(0, Math.min(1, v / safeMax));
        const g = Math.round(255 * t);
        bctx.fillStyle = `rgb(${g},${g},${g})`;
        bctx.fillRect(bx * binSize, by * binSize, binSize, binSize);
      }
    }

    const blurPx = Math.max(12, (HEAT.sigmaPx ?? 30));
    octx.filter = `blur(${blurPx}px)`;
    octx.drawImage(bins, 0, 0);
    octx.filter = 'none';

    const imgData = octx.getImageData(0, 0, off.width, off.height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i];
      const tLin = gray / 255;
      const t = Math.pow(tLin, HEAT.intensityGamma);
      const r = Math.max(0, Math.min(1, 1.5 - Math.abs(4 * t - 3)));
      const g = Math.max(0, Math.min(1, 1.5 - Math.abs(4 * t - 2)));
      const b = Math.max(0, Math.min(1, 1.5 - Math.abs(4 * t - 1)));
      imgData.data[i]     = Math.round(r * 255);
      imgData.data[i + 1] = Math.round(g * 255);
      imgData.data[i + 2] = Math.round(b * 255);
      imgData.data[i + 3] = Math.round(255 * t);
    }
    octx.putImageData(imgData, 0, 0);
  }, [ensureOffscreenMatchesBase]);
    
  const materializeFromCounts = useCallback(async (counts) => {
    renderCountsToOffscreen(counts);
    const off = heatmapOffscreenRef.current;
    if (!off) return null;
    const dataUrl = off.toDataURL('image/png');
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror  = (e) => reject(e);
      img.src = dataUrl;
    });
  }, [renderCountsToOffscreen]);

  const countsFromPath = useCallback((path) => {
    const meta = heatmapMetaRef.current;
    const { gridW, gridH, binSize } = meta;
    const len = gridW * gridH;
    const total = new Float32Array(len);
    const add   = new Float32Array(len);
    const del   = new Float32Array(len);
    let max = 0;

    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      const dt = Math.max(0, (b.t ?? 0) - (a.t ?? 0));
      if (!Number.isFinite(dt)) continue;

      const dx = b.x - a.x, dy = b.y - a.y;
      const dist = Math.hypot(dx, dy);
      const steps = Math.max(1, Math.round(dist / (binSize * (HEAT.rebuildStepScale || 1))));
      const dtPer = dt / steps;
      const mode = b.mode || a.mode || 'add';

      for (let s = 0; s <= steps; s++) {
        const x = a.x + (dx * s) / steps;
        const y = a.y + (dy * s) / steps;
        const bx = Math.max(0, Math.min(gridW - 1, Math.floor(x / binSize)));
        const by = Math.max(0, Math.min(gridH - 1, Math.floor(y / binSize)));
        const idx = by * gridW + bx;

        total[idx] += dtPer;
        if (mode === 'delete') del[idx] += dtPer; else add[idx] += dtPer;
        if (total[idx] > max) max = total[idx];
      }
    }

    return { total, add, del, max };
  }, []);
    
  const rebuildHeatmapsForSelectionAndMaterialize = useCallback(async (selectionForHeatmap) => {
    const base = loadedImages?.enhanced;
    if (!base) return;

    const path = buildTrimmedPathForSelection(selectionForHeatmap);
    if (!path.length) {
      setLoadedImages(prev => ({ ...prev, heatmap: null, heatmapAdd: null, heatmapDelete: null }));
      setLayersConfig(prev => ({
        ...prev,
        heatmap:      { ...prev.heatmap,      image: null },
        heatmapAdd:   { ...prev.heatmapAdd,   image: null },
        heatmapDelete:{ ...prev.heatmapDelete,image: null },
      }));
      return;
    }

    const { total, add, del } = countsFromPath(path);
    const [imgCombined, imgAdd, imgDel] = await Promise.all([
      materializeFromCounts(total),
      materializeFromCounts(add),
      materializeFromCounts(del),
    ]);

    setLoadedImages(prev => ({
      ...prev,
      heatmap: imgCombined || null,
      heatmapAdd: imgAdd || null,
      heatmapDelete: imgDel || null,
    }));
    setLayersConfig(prev => ({
      ...prev,
      heatmap:      { ...prev.heatmap,      image: imgCombined || null },
      heatmapAdd:   { ...prev.heatmapAdd,   image: imgAdd || null },
      heatmapDelete:{ ...prev.heatmapDelete,image: imgDel || null },
    }));
  }, [loadedImages, countsFromPath, materializeFromCounts, buildTrimmedPathForSelection]);
    
  const composeQualityOverlay = useCallback(async (minutiaePoints) => {
    const baseImg = loadedImages?.enhanced;
    const clarityImg = loadedImages?.clarity;
    if (!baseImg || !clarityImg) return null;

    const clarityCanvas = document.createElement('canvas');
    clarityCanvas.width = baseImg.width;
    clarityCanvas.height = baseImg.height;
    const clarityCtx = clarityCanvas.getContext('2d', { willReadFrequently: true });
    clarityCtx.imageSmoothingEnabled = false;
    clarityCtx.drawImage(clarityImg, 0, 0, baseImg.width, baseImg.height);

    const qualityCanvas = document.createElement('canvas');
    qualityCanvas.width = baseImg.width;
    qualityCanvas.height = baseImg.height;
    const qctx = qualityCanvas.getContext('2d');

    const sampleColor = (x, y) => {
      const d = clarityCtx.getImageData(Math.round(x), Math.round(y), 1, 1).data;
      const sum = d[0] + d[1] + d[2];
      if (sum < 15) return 'rgba(128,128,128,1)';
      return `rgba(${d[0]},${d[1]},${d[2]},1)`;
    };

    const normalize = (arr) => (arr || []).map(pt => ({
      x: pt.x ?? pt.position?.x,
      y: pt.y ?? pt.position?.y,
      angle: (typeof pt.angle === 'number')
        ? pt.angle
        : (typeof pt.angleDeg === 'number' ? (pt.angleDeg * Math.PI) / 180 : undefined),
      dx: pt.dx, dy: pt.dy
    })).filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));

    const pts = normalize(minutiaePoints);
    const R = 5;
    pts.forEach(pt => {
      const { x, y } = pt;
      const color = sampleColor(x, y);
      qctx.fillStyle = color;
      qctx.strokeStyle = color;
      qctx.lineWidth = 2;

      qctx.beginPath();
      qctx.arc(x, y, R, 0, Math.PI * 2);
      qctx.fill();

      let vx = 0, vy = 0;
      if (typeof pt.angle === 'number') { vx = Math.cos(pt.angle); vy = Math.sin(pt.angle); }
      else if (Number.isFinite(pt.dx) && Number.isFinite(pt.dy)) {
        const len = Math.hypot(pt.dx, pt.dy) || 1; vx = pt.dx / len; vy = pt.dy / len;
      }
      if (vx || vy) {
        const L = 18;
        qctx.beginPath();
        qctx.moveTo(x, y);
        qctx.lineTo(x + vx * L, y + vy * L);
        qctx.stroke();
      }
    });

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror  = (e) => reject(e);
      img.src = qualityCanvas.toDataURL('image/png');
    });
  }, [loadedImages]);

  const handleAnalyze = useCallback(async () => {
    if (!sessionDetails?.id) { showToast('No active session.', 3000, 'error'); return; }
    if (!hasCompleted)       { showToast('Complete the session first.', 3000, 'info'); return; }

    setIsProcessing(true);
    showToast('Analyzing...', 0, 'loading');

    try {
      const saved = await api.getTrackingData(sessionDetails.id).catch(() => null);
      HDBG('Analyze: saved path len =', Array.isArray(saved?.path) ? saved.path.length : 0);

      revealLayersForAnalysis();

      const heatSel = layersConfig?.heatmap?.currentSession ?? 'all';
      await rebuildHeatmapsForSelectionAndMaterialize(heatSel);

      const mergedPts = [
        ...(Array.isArray(saved?.features) ? saved.features : []),
        ...features.map(f => ({ x: f.x, y: f.y, angleDeg: f.angleDeg })),
      ];
      const qualityImg = await composeQualityOverlay(mergedPts);
      if (qualityImg) {
        setLoadedImages(prev => ({ ...prev, quality: qualityImg }));
        setLayersConfig(prev => ({ ...prev, quality: { ...prev.quality, image: qualityImg, visible: true, opacity: 0.9 } }));
      }

      const playSel = layersConfig?.playback?.currentSession ?? 'all';
      playbackSelectionRef.current = playSel;
      playbackPathRef.current = buildTrimmedPathForSelection(playSel);
      let duration = 0;
      const p = playbackPathRef.current;
      if (p.length >= 2) duration = Math.max(0, (p[p.length - 1].t - p[0].t) / 1000);
      console.log('[DEMO Analyze] Playback path:', p.length, 'points, duration:', duration.toFixed(2), 'seconds');
      console.log('[DEMO Analyze] toolSessions:', toolSessions.length, 'sessions');
      if (toolSessions.length > 0) {
        console.log('[DEMO Analyze] First session pathPoints:', toolSessions[0]?.pathPoints?.length || 0);
      }
      setPlaybackState(s => ({ ...s, duration, currentTime: 0, progress: 0, isPlaying: false }));

      let metrics = null;
      try {
        const analyzeRes = await api.analyzeSession(sessionDetails.id);
        metrics = pickEquilibriumMetrics(analyzeRes);
      } catch (e) {
        console.warn('[DemoScopeContext] analyzeSession failed:', e?.message || e);
      }

      if (!metrics) {
        const pathAll = buildTrimmedPathForSelection('all');
        const eventsAll = buildTrimmedEventsForSelection('all');
        metrics = computeLocalEquilibriumMetrics(pathAll, eventsAll);
        console.warn('[DemoScopeContext] Using local equilibrium metrics fallback.');
        showToast('Analysis complete (local metrics).', 1800, 'success');
      } else {
        showToast('Analysis complete.', 1800, 'success');
      }

      setAnalysisResults(prev => ({
        ...(prev || {}),
        metrics: {
          ...(prev?.metrics || {}),
          equilibrium_metrics: metrics
        }
      }));

      setAnalysisEnabled(true);
    } catch (err) {
      showToast(`Analyze failed: ${err.message}`, 5000, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [
    sessionDetails, hasCompleted, revealLayersForAnalysis,
    rebuildHeatmapsForSelectionAndMaterialize, features, toolSessions,
    composeQualityOverlay, showToast, layersConfig, buildTrimmedPathForSelection, buildTrimmedEventsForSelection
  ]);
    
  const toggleClarityMap = useCallback(() => {
    if (!loadedImages.clarity) return;
    setLayersConfig(prev => {
      const nextVisible = !prev.clarity.visible;
      return { ...prev, clarity: { ...prev.clarity, visible: nextVisible } };
    });
  }, [loadedImages.clarity]);
    
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'c' || e.key === 'C') { e.preventDefault(); toggleClarityMap(); }
      if (e.code === 'Space') {
        e.preventDefault();
        setPlaybackState(s => {
          if (!analysisEnabled) return s;
          const toggleTo = !s.isPlaying;
          if (toggleTo) startPlayback(); else pausePlayback();
          return s;
        });
      }
      if (e.key === 'ArrowRight') {
        setPlaybackState(s => {
          if (!analysisEnabled) return s;
          const t = Math.min(s.duration, s.currentTime + 1);
          seekPlayback(t);
          return s;
        });
      }
      if (e.key === 'ArrowLeft') {
        setPlaybackState(s => {
          if (!analysisEnabled) return s;
          const t = Math.max(0, s.currentTime - 1);
          seekPlayback(t);
          return s;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleClarityMap, analysisEnabled]);
    
  useEffect(() => {
    const initializeDemo = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const details = await api.getSessionDetails('demo-session-1');
        setSessionDetails(details);

        const pairDetails = await api.getSyntheticPair('demo-pair-1');

        const { stitchedEnhancedUrl, stitchedClarityMapUrl } = pairDetails;
        if (!stitchedEnhancedUrl || !stitchedClarityMapUrl) throw new Error('Demo image URLs are missing.');

        showToast('Loading images...', 0, 'loading');

        const [enhancedImg, clarityImg] = await Promise.all([
          loadImage(stitchedEnhancedUrl),
          loadImage(stitchedClarityMapUrl),
        ]);

        const binSize = Math.max(HEAT.binSizeMin, Math.round(enhancedImg.width / HEAT.targetCols));
        const gridW = Math.ceil(enhancedImg.width / binSize);
        const gridH = Math.ceil(enhancedImg.height / binSize);
        heatmapMetaRef.current = {
          width: enhancedImg.width, height: enhancedImg.height,
          gridW, gridH, binSize, sigmaPx: HEAT.sigmaPx, max: 0, version: 0
        };

        const off = document.createElement('canvas');
        off.width = enhancedImg.width;
        off.height = enhancedImg.height;
        heatmapOffscreenRef.current = off;

        const totalLen = gridW * gridH;
        heatmapCountsRef.current = new Float32Array(totalLen);
        heatAddCountsRef.current = new Float32Array(totalLen);
        heatDelCountsRef.current = new Float32Array(totalLen);

        HDBG('Init grid', { binSize, gridW, gridH, width: enhancedImg.width, height: enhancedImg.height });

        setLoadedImages({ enhanced: enhancedImg, clarity: clarityImg, quality: null, heatmap: null, heatmapAdd: null, heatmapDelete: null });

        // Initialize layers - base and minutiae layers are available during tracking
        // All other layers remain disabled until Analyze is pressed
        setLayersConfig(prev => ({
          ...prev,
          base:          {...prev.base, image: enhancedImg, disabled: false, visible: true },
          minutiae:      { ...prev.minutiae,      disabled: false, visible: true },
          heatmap:       { ...prev.heatmap,       disabled: true,  visible: false, image: null },
          heatmapAdd:    { ...prev.heatmapAdd,    disabled: true,  visible: false, image: null },
          heatmapDelete: { ...prev.heatmapDelete, disabled: true,  visible: false, image: null },
          tracking:      { ...prev.tracking,      disabled: true,  visible: false, image: null },
          clarity:       { ...prev.clarity,       disabled: false, visible: false, image: clarityImg },
          quality:       { ...prev.quality,       disabled: true,  visible: false, image: null },
          playback:      { ...prev.playback,      disabled: true,  visible: false, currentSession: 'all' },
        }));
        
        showToast("Demo ready. Click 'Start Tracking' to begin.", 3000, 'success');
      } catch (err) {
        const msg = err?.message || 'Failed to load session data.';
        setError(msg);
        showToast(msg, 0, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    initializeDemo();
  }, [showToast, loadImage]);
    
  const addFeature = useCallback((x, y, angleDeg = null) => {
    const f = { id: Date.now(), x, y, angleDeg, createdAt: Date.now() };
    setFeatures(prev => [...prev, f]);
    pushSessionEvent('add', x, y, NOW(), { angleDeg });
    return f.id;
  }, [pushSessionEvent]);

  const removeFeatureById = useCallback((id) => {
    setFeatures(prev => prev.filter(f => f.id !== id));
    pushSessionEvent('delete', 0, 0, NOW());
  }, [pushSessionEvent]);

  const removeNearestFeature = useCallback((x, y, maxDistPx = 12) => {
    let idx = -1, best = Infinity;
    let removed = null;
    setFeatures(prev => {
      prev.forEach((f, i) => {
        const d = Math.hypot(f.x - x, f.y - y);
        if (d < best) { best = d; idx = i; }
      });
      if (idx >= 0 && best <= maxDistPx) {
        removed = prev[idx];
        return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
      }
      return prev;
    });
    if (removed) pushSessionEvent('delete', x, y, NOW());
    return !!removed;
  }, [pushSessionEvent]);

  const onPointerDown = useCallback((x, y) => {
    if (!isTracking) return;
    ensureActiveSession();
    hudMouseRef.current = { x, y };
    dragRef.current = { isDown: true, startX: x, startY: y, lastX: x, lastY: y };
    appendPathPoint(x, y, NOW());
    pendingPosRef.current = { x, y };
  }, [isTracking, ensureActiveSession, appendPathPoint]);

  const onPointerMove = useCallback((x, y) => {
    if (!isTracking) return;
    hudMouseRef.current = { x, y };
    const d = dragRef.current;
    if (d.isDown) { d.lastX = x; d.lastY = y; }
    appendPathPoint(x, y, NOW());
    pendingPosRef.current = { x, y };
  }, [isTracking, appendPathPoint]);

  const onPointerUp = useCallback((x, y) => {
    if (!isTracking) return;
    hudMouseRef.current = { x, y };
    const d = dragRef.current;
    const mode = toolStateRef.current.current;

    if (d.isDown) {
      d.isDown = false;
      if (mode === 'add') {
        const dx = d.lastX - d.startX;
        const dy = d.lastY - d.startY;
        const len = Math.hypot(dx, dy);
        const angleDeg = len > 0 ? (Math.atan2(dy, dx) * 180) / Math.PI : null;
        addFeature(d.startX, d.startY, angleDeg);
      } else {
        const removed = removeNearestFeature(x, y, 14);
        if (!removed) showToast('No minutia near cursor to delete.', 1200, 'info');
      }
    }

    appendPathPoint(x, y, NOW());
    pendingPosRef.current = { x, y };
  }, [isTracking, addFeature, removeNearestFeature, showToast, appendPathPoint]);

  const onPointerLeave = useCallback(() => {
    dragRef.current.isDown = false;
  }, []);

  const shouldDrawMinutiaeFeatures = layersConfig.minutiae.visible;

  const getHudMousePosition = useCallback(() => hudMouseRef.current, []);
  const getActiveDrag = useCallback(() => dragRef.current, []);
    
  const drawTrackingPath = useCallback((ctx) => {
    if (!ctx) return;

    const sel = layersConfig?.tracking?.currentSession ?? 'all';
    const sessions = toolSessions;
    const selNorm = normalizeSelection(sel);
    const ids = selNorm === 'all' ? sessions.map(s => s.id) : selNorm;

    ctx.save();
    ctx.globalAlpha = layersConfig.tracking.opacity ?? 1.0;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    sessions.forEach(s => {
      if (selNorm !== 'all' && !ids.includes(s.id)) return;
      const pts = cleanTrackingData(s);
      if (pts.length < 2) return;

      const color = s.type === 'delete' ? '#ff6b6b' : '#00ffff';
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.6;
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    });

    ctx.restore();
  }, [layersConfig?.tracking?.currentSession, layersConfig?.tracking?.opacity, toolSessions, cleanTrackingData, normalizeSelection]);
    
  const drawPlaybackPath = useCallback((ctx) => {
    if (!ctx) return;
    if (!layersConfig?.playback?.visible) return;

    const p = playbackPathRef.current;
    if (!p || p.length < 2) return;

    const { currentTime } = playbackState;
    const t0 = p[0].t;
    const cutoff = t0 + currentTime * 1000;

    ctx.save();
    ctx.globalAlpha = layersConfig.playback.opacity ?? 1.0;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;

    let started = false;
    let currentMode = p[0].mode || 'add';
    ctx.strokeStyle = currentMode === 'delete' ? '#ff6b6b' : '#ffd166';

    for (let i = 1; i < p.length; i++) {
      const a = p[i - 1], b = p[i];
      if (b.t > cutoff) break;

      const nextMode = b.mode || a.mode || 'add';
      const color = nextMode === 'delete' ? '#ff6b6b' : '#ffd166';
      if (!started) { ctx.beginPath(); ctx.moveTo(a.x, a.y); started = true; }
      if (nextMode !== currentMode) {
        ctx.stroke();
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.moveTo(a.x, a.y);
        currentMode = nextMode;
      }
      ctx.lineTo(b.x, b.y);
    }
    if (started) ctx.stroke();
    ctx.restore();

    const events = buildTrimmedEventsForSelection(layersConfig?.playback?.currentSession ?? 'all');
    
    ctx.save();
    for (const ev of events) {
      if (ev.t > cutoff) break;
      if (ev.type === 'add') {
        ctx.fillStyle = '#00ff00';
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ev.x, ev.y, 3, 0, Math.PI * 2);
        ctx.fill();
        if (typeof ev.angleDeg === 'number') {
          const L = 18;
          const rad = (ev.angleDeg * Math.PI) / 180;
          const x2 = ev.x + Math.cos(rad) * L;
          const y2 = ev.y + Math.sin(rad) * L;
          ctx.beginPath();
          ctx.moveTo(ev.x, ev.y);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      } else if (ev.type === 'delete') {
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 2;
        const r = 5;
        ctx.beginPath();
        ctx.moveTo(ev.x - r, ev.y - r);
        ctx.lineTo(ev.x + r, ev.y + r);
        ctx.moveTo(ev.x + r, ev.y - r);
        ctx.lineTo(ev.x - r, ev.y + r);
        ctx.stroke();
      }
    }
    ctx.restore();
  }, [layersConfig?.playback?.visible, layersConfig?.playback?.opacity, playbackState, buildTrimmedEventsForSelection]);
    
  const rebuildPlaybackPathForSelection = useCallback((selection) => {
    playbackSelectionRef.current = selection ?? 'all';
    playbackPathRef.current = buildTrimmedPathForSelection(playbackSelectionRef.current);
    let duration = 0;
    const p = playbackPathRef.current;
    if (p.length >= 2) duration = Math.max(0, (p[p.length - 1].t - p[0].t) / 1000);
    setPlaybackState(s => ({ ...s, duration, currentTime: Math.min(s.currentTime, duration), progress: duration ? (Math.min(s.currentTime, duration) / duration) : 0 }));
  }, [buildTrimmedPathForSelection]);

  // Use a ref to track playback state for the animation loop (avoids stale closures)
  const playbackStateRef = useRef(playbackState);
  useEffect(() => { playbackStateRef.current = playbackState; }, [playbackState]);

  const startPlayback = useCallback(() => {
    if (!analysisEnabled) return;

    const sel = layersConfig?.playback?.currentSession ?? 'all';
    if (!playbackPathRef.current || playbackPathRef.current.length < 2 || sel !== playbackSelectionRef.current) {
      rebuildPlaybackPathForSelection(sel);
    }

    // Check if we have valid duration before starting
    const path = playbackPathRef.current;
    if (!path || path.length < 2) {
      console.warn('[Playback] No valid path data to play');
      return;
    }

    if (!visibilitySnapshotRef.current) {
      const snap = {};
      Object.keys(layersConfig).forEach(k => { snap[k] = !!layersConfig[k].visible; });
      visibilitySnapshotRef.current = snap;
      setLayersConfig(prev => ({
        ...prev,
        base:     { ...prev.base,     visible: true },
        playback: { ...prev.playback, visible: true },
        minutiae: { ...prev.minutiae, visible: false },
        tracking: { ...prev.tracking, visible: false },
        heatmap:  { ...prev.heatmap,  visible: false },
        heatmapAdd:   { ...prev.heatmapAdd,   visible: false },
        heatmapDelete:{ ...prev.heatmapDelete, visible: false },
        clarity:  { ...prev.clarity,  visible: false },
        quality:  { ...prev.quality,  visible: false },
      }));
    }

    // Cancel any existing animation frame
    if (playbackRafRef.current) {
      cancelAnimationFrame(playbackRafRef.current);
      playbackRafRef.current = 0;
    }

    // Check if at end and need to restart
    const currentState = playbackStateRef.current;
    const atEnd = currentState.currentTime >= currentState.duration - 0.01;
    const startTime = atEnd ? 0 : currentState.currentTime;

    // Set isPlaying to true, and restart from beginning if at the end
    setPlaybackState(s => {
      if (s.duration <= 0) return s;
      return { ...s, isPlaying: true, currentTime: startTime, progress: s.duration ? (startTime / s.duration) : 0 };
    });

    // Update ref immediately so tick has correct starting point
    playbackStateRef.current = {
      ...currentState,
      isPlaying: true,
      currentTime: startTime,
      progress: currentState.duration ? (startTime / currentState.duration) : 0
    };

    // Start the animation loop
    playbackLastTsRef.current = performance.now();

    const tick = () => {
      const state = playbackStateRef.current;

      if (!state.isPlaying) {
        playbackRafRef.current = 0;
        return;
      }

      const now = performance.now();
      const dt = (now - (playbackLastTsRef.current || now)) / 1000 * (state.speed || 1);
      playbackLastTsRef.current = now;

      let t = state.currentTime + dt;
      let stillPlaying = true;

      if (t >= state.duration) {
        t = state.duration;
        stillPlaying = false;
      }

      // Update ref immediately for next tick
      playbackStateRef.current = {
        ...state,
        currentTime: t,
        progress: state.duration ? (t / state.duration) : 0,
        isPlaying: stillPlaying
      };

      setPlaybackState(s => ({
        ...s,
        currentTime: t,
        progress: s.duration ? (t / s.duration) : 0,
        isPlaying: stillPlaying
      }));

      if (stillPlaying) {
        playbackRafRef.current = requestAnimationFrame(tick);
      } else {
        playbackRafRef.current = 0;
      }
    };

    // Start immediately
    playbackRafRef.current = requestAnimationFrame(tick);
  }, [analysisEnabled, layersConfig, rebuildPlaybackPathForSelection]);

  const restoreVisibilitySnapshot = useCallback(() => {
    const snap = visibilitySnapshotRef.current;
    if (!snap) return;
    setLayersConfig(prev => {
      const next = { ...prev };
      Object.keys(snap).forEach(k => {
        if (next[k]) next[k] = { ...next[k], visible: !!snap[k] };
      });
      return next;
    });
    visibilitySnapshotRef.current = null;
  }, []);

  const pausePlayback = useCallback(() => {
    setPlaybackState(s => ({ ...s, isPlaying: false }));
    if (playbackRafRef.current) { cancelAnimationFrame(playbackRafRef.current); playbackRafRef.current = 0; }
  }, []);

  const stopPlayback = useCallback(() => {
    if (playbackRafRef.current) { cancelAnimationFrame(playbackRafRef.current); playbackRafRef.current = 0; }
    setPlaybackState(s => ({ ...s, isPlaying: false, currentTime: 0, progress: 0 }));
    restoreVisibilitySnapshot();
  }, [restoreVisibilitySnapshot]);

  const seekPlayback = useCallback((timeSeconds) => {
    setPlaybackState(s => {
      const t = Math.max(0, Math.min(s.duration, timeSeconds));
      return { ...s, currentTime: t, progress: s.duration ? (t / s.duration) : 0 };
    });
  }, []);

  const setPlaybackSpeed = useCallback((speed) => {
    setPlaybackState(s => ({ ...s, speed: Math.max(0.1, Math.min(8, speed || 1)) }));
  }, []);

  useEffect(() => () => {
    if (playbackRafRef.current) cancelAnimationFrame(playbackRafRef.current);
  }, []);

  useEffect(() => {
    if (!analysisEnabled) return;
    const sel = layersConfig?.playback?.currentSession ?? 'all';
    if (sel !== playbackSelectionRef.current) {
      rebuildPlaybackPathForSelection(sel);
    }
  }, [analysisEnabled, layersConfig?.playback?.currentSession, rebuildPlaybackPathForSelection]);

  useEffect(() => {
    if (!analysisEnabled) return;
    const heatSel = layersConfig?.heatmap?.currentSession ?? 'all';
    rebuildHeatmapsForSelectionAndMaterialize(heatSel);
  }, [analysisEnabled, layersConfig?.heatmap?.currentSession]);

  const materializeAnalyzedLayers = useCallback(async () => {
    try { 
      revealLayersForAnalysis();
    } catch(e) {
      console.error('[DemoScopeContext] revealLayersForAnalysis failed:', e);
    }

    try {
      const sel = (layersConfig?.heatmap?.currentSession) || 'all';
      await rebuildHeatmapsForSelectionAndMaterialize(sel);
    } catch (e) {
      console.error('[DemoScopeContext] materialize: heatmap failed', e);
    }

    try {
      const pts = Array.isArray(features) ? features.map(f => ({ x: f.x, y: f.y, angleDeg: f.angleDeg })) : [];
      const qImg = await composeQualityOverlay(pts);
      if (qImg) {
        setLoadedImages(prev => ({ ...prev, quality: qImg }));
        setLayersConfig(prev => ({ ...prev, quality: { ...prev.quality, image: qImg, visible: true, opacity: 0.9, disabled: false } }));
      }
    } catch (e) {
      console.error('[DemoScopeContext] materialize: quality failed', e);
    }

    try {
      const playSel = layersConfig?.playback?.currentSession ?? 'all';
      const playPath = buildTrimmedPathForSelection(playSel);
      playbackSelectionRef.current = playSel;
      playbackPathRef.current = Array.isArray(playPath) ? playPath.slice() : [];
      let duration = 0;
      const p = playbackPathRef.current || [];
      if (p.length >= 2) duration = Math.max(0, (p[p.length - 1].t - p[0].t) / 1000);
      setPlaybackState(s => ({ ...s, duration, currentTime: 0, progress: 0, isPlaying: false }));
      setLayersConfig(prev => ({ ...prev, playback: { ...prev.playback, disabled: false, visible: true } }));
    } catch (e) {
      console.error('[DemoScopeContext] materialize: playback failed', e);
    }
      
    try {
      const pathAll = buildTrimmedPathForSelection('all');
      const eventsAll = buildTrimmedEventsForSelection('all');
      const equilibriumMetrics = computeLocalEquilibriumMetrics(pathAll, eventsAll);
      
      setAnalysisResults(prev => ({
        ...(prev || {}),
        metrics: {
          ...(prev?.metrics || {}),
          equilibrium_metrics: equilibriumMetrics
        }
      }));
    } catch (e) {
      console.error('[DemoScopeContext] materialize: equilibrium metrics failed', e);
    }

    try {
      setAnalysisEnabled(true);
    } catch (e) {
      console.error('[DemoScopeContext] materialize: enable analysis failed', e);
    }

    return true;
  }, [
    features, layersConfig, revealLayersForAnalysis, rebuildHeatmapsForSelectionAndMaterialize, composeQualityOverlay,
    setLoadedImages, setLayersConfig, buildTrimmedPathForSelection, setPlaybackState,
    buildTrimmedEventsForSelection, computeLocalEquilibriumMetrics, setAnalysisResults
  ]);
    
  const setMinutiae = useCallback((arr) => {
    const next = Array.isArray(arr) ? arr : [];
    setFeatures(next);
  }, []);

  const setTrackingPath = useCallback((pathPoints) => {
    const pts = Array.isArray(pathPoints) ? pathPoints : [];
    if (!pts.length) { 
      try { setToolSessions([]); } catch {}
      return; 
    }
    
    const start = pts[0], end = pts[pts.length - 1] || start;
    const startTime = start.t ?? 0;
    const endTime = end.t ?? (startTime + 1);
    
    const synthetic = {
      id: 'review',
      type: 'add',
      events: [
        { type: 'start', t: startTime, x: start.x, y: start.y },
        { type: 'end', t: endTime, x: end.x, y: end.y }
      ],
      pathPoints: pts
    };
    
    try { 
      setToolSessions([synthetic]);
      activeSessionIdRef.current = 'review';
      playbackPathRef.current = pts.slice();
      
      const base = loadedImages?.enhanced;
      if (base && base.width && base.height) {
        const binSize = Math.max(HEAT.binSizeMin, Math.round(base.width / HEAT.targetCols));
        const gridW = Math.ceil(base.width / binSize);
        const gridH = Math.ceil(base.height / binSize);
        heatmapMetaRef.current = {
          width: base.width, height: base.height,
          gridW, gridH, binSize, sigmaPx: HEAT.sigmaPx, max: 0, version: 0
        };
        
        const totalLen = gridW * gridH;
        heatmapCountsRef.current = new Float32Array(totalLen);
        heatAddCountsRef.current = new Float32Array(totalLen);
        heatDelCountsRef.current = new Float32Array(totalLen);
      }
    } catch(e) {
      console.error('[DemoScopeContext] setTrackingPath failed:', e);
    }
  }, [loadedImages]);

  const ensureDefaultLayers = useCallback(() => {
    const hasLayers = layersConfig && Object.keys(layersConfig).length > 0;
    if (hasLayers) return true;
    
    setLayersConfig(initialLayerConfig);
    return true;
  }, [layersConfig]);

  const setLayersReadyFlag = useCallback((ready) => {
    setLayersReady(ready);
  }, []);

  const value = useMemo(() => ({
    isLoading, isProcessing, error, sessionDetails, layersConfig,
    isTracking, elapsedTime, hasTrackingCompleted, selectedConclusion,
    conclusionOptions, toolState, toolSessions, analysisResults, playbackState,
    hasCompleted, analysisEnabled, statusMessage,

    isReadOnly, isReviewMode, isSyntheticPreview, isImagePairPreview, layersReady, metrics,
    setReadOnly: setIsReadOnly, setReviewMode: setIsReviewMode,
    setLayersReady: setLayersReadyFlag, setMetrics,

    // Confirm Modal
    confirmModal, showConfirmModal, closeConfirmModal, handleConfirmModalConfirm,

    canStartTracking, canAnalyze, canComplete, canEndSession,

    loadedImages, showEnhanced, showClarityMap,
    setMinutiae, setTrackingPath, materializeAnalyzedLayers, ensureDefaultLayers,

    startTracking, stopTrackingNow,
    setLayersConfig, updateLayer, setSelectedConclusion, setAnalysisResults,
    handleToggleTool, handleSelectConclusion,
    handleCompleteSession, handleAnalyze, handleEndSession, handleCancelSession,
    showToast, toggleClarityMap,

    revealLayersForAnalysis, rebuildHeatmapsForSelectionAndMaterialize, composeQualityOverlay,

    features, addFeature, removeFeatureById,
    onPointerDown, onPointerMove, onPointerUp, onPointerLeave,
    shouldDrawMinutiaeFeatures,

    drawTrackingPath,

    drawPlaybackPath,
    startPlayback, pausePlayback, stopPlayback, seekPlayback, setPlaybackSpeed,

    getHudMousePosition, getActiveDrag,
    onExit,
  }), [
    isLoading, isProcessing, error, sessionDetails, layersConfig,
    isTracking, elapsedTime, hasTrackingCompleted, selectedConclusion,
    conclusionOptions, toolState, toolSessions, analysisResults, playbackState,
    hasCompleted, analysisEnabled, statusMessage,
    isReadOnly, isReviewMode, isSyntheticPreview, isImagePairPreview, layersReady, metrics,
    setIsReadOnly, setIsReviewMode, setLayersReadyFlag, setMetrics,
    confirmModal, showConfirmModal, closeConfirmModal, handleConfirmModalConfirm,
    canStartTracking, canAnalyze, canComplete, canEndSession,
    loadedImages, showEnhanced, showClarityMap,
    setMinutiae, setTrackingPath, materializeAnalyzedLayers, ensureDefaultLayers,
    startTracking, stopTrackingNow, updateLayer, setSelectedConclusion, setAnalysisResults,
    handleToggleTool, handleSelectConclusion, handleCompleteSession, handleAnalyze,
    handleEndSession, handleCancelSession, showToast, toggleClarityMap,
    revealLayersForAnalysis, rebuildHeatmapsForSelectionAndMaterialize, composeQualityOverlay,
    features, addFeature, removeFeatureById, onPointerDown, onPointerMove, onPointerUp, onPointerLeave,
    shouldDrawMinutiaeFeatures, drawTrackingPath, drawPlaybackPath, startPlayback, pausePlayback,
    stopPlayback, seekPlayback, setPlaybackSpeed, getHudMousePosition, getActiveDrag, onExit
  ]);

  return (
    <DemoScopeContext.Provider value={value}>
      {children}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmModalConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        variant={confirmModal.variant}
      />
    </DemoScopeContext.Provider>
  );
};